import {
	ActionBar,
	Button,
	ContentCard,
	InputSwitch,
	Text,
	decoratedAttributes,
	useToggle,
} from '@innovyze/stylovyze';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useGlobalization } from '@Translations/useGlobalization';
import { addRouteNamespace } from '@Utils/actions';
import {
	EditIntegrationParams,
	IntegrationType,
	VaparSettingsFormValues,
} from '../../types/integration.types';
import {
	createIntegration,
	deleteIntegration,
	updateIntegration,
} from '../actions/integrations.actions';
import {
	VaparSettingsFooter,
	ButtonContainer,
	Container,
} from './VaparSettings.styles';
import VaparSettingsValues from './VaparSettingsValues';
import IntegrationInfoDisplay from './IntegrationInfoDisplay';
import DeleteIntegrationModal from '../arcgis/ArcGisSettings/DeleteIntegrationModal';
import VaparAuthDialog from './VaparAuthDialog';
import VaparAuthenticateButton from './VaparAuthenticateButton';
import VaparAuthFailedDialog from './VaparAuthFailedDialog';

export default function VaparSettings() {
	const dispatch = useDispatch();
	const { t } = useGlobalization();
	const history = useHistory();
	const { values, isValid, dirty } =
		useFormikContext<VaparSettingsFormValues>();
	const [isSubmitted, setSubmitted] = useState(false);
	const [isDeleteModalOpen, toggleDeleteModalOpen] = useToggle(false);
	const [vaparAuthOpen, setVaparAuthOpen] = useState(false);
	const [vaparAuthFailedOpen, setVaparAuthFailedOpen] = useState(false);
	const handleCancel = () => history.push(addRouteNamespace('integrations'));
	const handleDelete = () => dispatch(deleteIntegration(values._id ?? ''));
	const isEditMode = values._id != null;
	const isCreateMode = !isEditMode;
	const hasNoName = values.name == null || values.name.length === 0;

	const handleSaveIntegration = () => {
		setSubmitted(true);
		const now = new Date();
		if (values.name) {
			let integration: EditIntegrationParams = {
				_id: values._id,
				name: values.name,
				type: 'vapar',
				description: values.description ?? 'Vapar Integration',
				createdAt: now,
				lastModifiedTimestamp: now,
				enabledAiDefault: values.enabledAiDefault,
			};
			if (values.integrationInfo) {
				integration = {
					...integration,
					isEnabled: values.integrationInfo.toBeEnabled ?? false,
					ssl: values.ssl ?? false,
					isValid: values.integrationInfo?.isValid,
					isUKRegion: values.integrationInfo?.isUKRegion,
				};
			}
			if (values.integrationTokenInfo) {
				integration = {
					...integration,
					organizationId: values.integrationTokenInfo.organizationId,
					refreshTokenExpires:
						values.integrationTokenInfo.refreshTokenExpires,
					tokenExpires: values.integrationTokenInfo.tokenExpires,

					clientId: values.integrationTokenInfo.clientId,
					username: values.integrationTokenInfo?.username,
					token: values.integrationTokenInfo.token,
					refreshToken: values.integrationTokenInfo.refreshToken,
					portal: values.integrationTokenInfo?.portal,
					enabledAiDefault: values.enabledAiDefault,
					isValid: values.integrationInfo?.isValid,
					isUKRegion: values.integrationTokenInfo?.isUKRegion,
				};
			}

			if (isEditMode) {
				dispatch(updateIntegration(integration));
			}
			if (isCreateMode) {
				dispatch(createIntegration(integration));
			}
		}
	};

	const adpAttrs = decoratedAttributes({
		name: values.name ?? '',
		description: values.description ?? '',
		type: IntegrationType.Vapar,
		enabled: (values.integrationInfo?.toBeEnabled ?? false).toString(),
	});
	return (
		<ContentCard
			header={
				<ActionBar>
					<Text variant="heading-medium-strong">
						{t('VAPAR Config')}
					</Text>
					<InputSwitch
						fieldAttrs={{ name: 'integrationInfo.toBeEnabled' }}
						switchProps={{}}
						label={
							values.integrationInfo?.toBeEnabled
								? t('Enabled')
								: t('Disabled')
						}
					/>
				</ActionBar>
			}
			footer={
				<ActionBar
					style={{
						justifyContent: isEditMode
							? 'space-between'
							: 'flex-end',
					}}>
					{isEditMode && (
						<>
							<DeleteIntegrationModal
								open={isDeleteModalOpen}
								onCancel={toggleDeleteModalOpen}
								onDelete={handleDelete}
							/>
							<Button
								dataCy="delete-integration"
								variant="contained"
								color="error"
								onClick={toggleDeleteModalOpen}>
								{t('Delete')}
							</Button>
						</>
					)}
					<VaparSettingsFooter>
						<Button
							dataCy="cancel-integration"
							variant="contained"
							color="secondary"
							onClick={handleCancel}>
							{t('Cancel')}
						</Button>

						<Button
							adp-click="true"
							dataCy="save-integration"
							variant="contained"
							color="primary"
							disabled={!isValid || !dirty || isSubmitted}
							onClick={handleSaveIntegration}
							{...adpAttrs}>
							{t('Save')}
						</Button>
					</VaparSettingsFooter>
				</ActionBar>
			}
			applyHeaderDivider>
			<Container>
				<VaparAuthDialog
					open={vaparAuthOpen}
					onCancel={() => setVaparAuthOpen(false)}
					onAuthorize={() => {
						setVaparAuthOpen(false);
					}}
				/>
				<VaparAuthFailedDialog
					open={vaparAuthFailedOpen}
					onCancel={() => setVaparAuthFailedOpen(false)}
				/>
				<VaparSettingsValues />
				{values.integrationTokenInfo ? (
					<IntegrationInfoDisplay
						setVaparAuthOpen={setVaparAuthOpen}
					/>
				) : (
					<ButtonContainer>
						<VaparAuthenticateButton
							disabled={!isValid || (!dirty && hasNoName)}
							setVaparAuthOpen={setVaparAuthOpen}
						/>
					</ButtonContainer>
				)}
			</Container>
		</ContentCard>
	);
}
